import type { PublicPageProfile } from "@/lib/profile.functions";
import { buildPageMetadata, SITE_URL } from "@/lib/seo";

export { SITE_URL };

type PageData =
  | {
      profile: PublicPageProfile | null;
      socials: { url: string; followers: number | null }[];
      services: { price_cents: number }[];
      sections?: { kind: string; title: string; subtitle?: string | null; year?: number | null }[];
      articles?: {
        slug: string;
        title: string;
        excerpt: string | null;
        cover_url: string | null;
        published_at: string;
      }[];
    }
  | undefined;

const PRO_TYPE_LABELS: Record<string, string> = {
  brand: "Brand",
  agency: "Agency",
  label: "Record Label",
  curator: "Curator",
  studio: "Studio",
};

/**
 * Shared head() builder for /user/$slug and /pro/$slug.
 *
 * Person profiles always use the creator's real avatar as the social image —
 * never the generic BeHot.Now preview when a photo exists. Only public fields
 * are exposed: no email, phone, billing, tax or account data.
 */
export function profileHead(variant: "user" | "pro", slug: string, data: PageData) {
  const path = `/${variant}/${slug}`;
  const profile = data?.profile ?? null;

  // Private, suspended or missing profile: no indexable metadata, no details.
  if (!profile || profile.is_public !== true || (variant === "pro" && !profile.pro_enabled)) {
    return buildPageMetadata({
      path,
      title: "Profile not available | BeHot.Now",
      description: "This profile is not available on BeHot.Now.",
      noindex: true,
    });
  }

  const isPro = variant === "pro";
  const name = isPro
    ? profile.pro_name || profile.display_name || `@${slug}`
    : profile.display_name || `@${slug}`;
  // A page-specific social preview always wins over the avatar/logo fallback.
  const image =
    profile.og_image_url ||
    (isPro ? profile.pro_logo_url || profile.avatar_url : profile.avatar_url) ||
    null;
  const category = (profile.niches ?? [])[0] ?? null;
  const profileType = isPro
    ? PRO_TYPE_LABELS[profile.pro_type ?? ""] ?? "Professional"
    : category
      ? `${category} creator`
      : "Creator";
  const location = profile.country_code ? ` in ${profile.country_code}` : "";
  const publicLinks = (data?.socials ?? []).map((s) => s.url).filter(Boolean);

  const ambassadorTitle = profile.is_ambassador
    ? profile.ambassador_title || "Official Platform Ambassador"
    : null;

  const title =
    profile.seo_title?.trim() ||
    (ambassadorTitle
      ? `${name} — ${ambassadorTitle} | BeHot.Now`
      : isPro
        ? `${name} — ${profileType} Profile & Open Offers | BeHot.Now`
        : `${name} — ${category ? `${category} ` : ""}Creator Profile | BeHot.Now`);

  const bio = (profile.headline || (isPro ? profile.pro_bio : profile.bio) || "").trim();
  const description = (
    profile.seo_description?.trim() ||
    bio ||
    `Discover ${name}, ${profileType}${location} on BeHot.Now. Explore the public profile, categories, links and latest activity.`
  ).slice(0, 300);

  const person = {
    "@context": "https://schema.org",
    "@type": "ProfilePage",
    url: `${SITE_URL}${path}`,
    mainEntity: {
      "@type": isPro ? "Organization" : "Person",
      name,
      ...(description ? { description } : {}),
      url: `${SITE_URL}${path}`,
      ...(image ? { image: [image] } : {}),
      ...(profile.country_code
        ? { address: { "@type": "PostalAddress", addressCountry: profile.country_code } }
        : {}),
      ...(publicLinks.length ? { sameAs: publicLinks } : {}),
      ...(ambassadorTitle ? { jobTitle: ambassadorTitle } : {}),
      ...((profile.niches ?? []).length ? { knowsAbout: profile.niches } : {}),
      ...(profile.website_url ? { mainEntityOfPage: profile.website_url } : {}),
    },
  };

  // Ambassador pages publish articles — expose them as an ItemList so search
  // engines can surface the individual pieces, not just the profile.
  const articles = data?.articles ?? [];
  const articleList =
    articles.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: `${name} — articles`,
          itemListElement: articles.slice(0, 20).map((a, i) => ({
            "@type": "ListItem",
            position: i + 1,
            item: {
              "@type": "Article",
              headline: a.title,
              ...(a.excerpt ? { description: a.excerpt } : {}),
              ...(a.cover_url ? { image: [a.cover_url] } : {}),
              datePublished: a.published_at,
              url: `${SITE_URL}${path}#${a.slug}`,
              author: { "@type": "Person", name },
            },
          })),
        }
      : null;

  return buildPageMetadata({
    path,
    title,
    description,
    ogTitle: title,
    ogDescription: description,
    ogType: isPro ? "website" : "profile",
    image,
    imageAlt: image
      ? `${name} profile picture on BeHot.Now`
      : `${name} on BeHot.Now`,
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: isPro ? "Professionals" : "Creators", path: "/marketplace" },
      { name, path },
    ],
    jsonLd: articleList ? [person, articleList] : person,
  });
}
